import { CURRENCY_DOTS } from '../../config/settings/regexes'

/**
 * To decorate the given value to a number with a thousands separator.
 */
export const formatNumberWithThousandSeparator = value => value.toString().replace(CURRENCY_DOTS, '$1.')

/**
 * Formats the given value with dots to commas.
 */
export const formatCommas = value => value.toString().replace(/\./, ',')

/**
 * Formats the given value to currency (xxx.xxx.xxx, ex: 1234 = 1.234).
 */
export const formatAmount = value => value?.toString().replace(CURRENCY_DOTS, '$1.')

/**
 * Formats the given value to percentage (xxx.xxx.xxx, ex: 1234 = 1.234).
 */
export const formatPercentage = value => `${formatCommas(value)}%`

/**
 * Formats the given value to higher units (e.g. 1.000.000 to 1M).
 */
export const minFormatter = value => {
  switch (true) {
    case value > 1000 && value < 1000000: {
      return `${formatCommas((value / 1000).toFixed(2))}K`
    }
    case value >= 1000000 && value < 1000000000: {
      return `${formatCommas((value / 1000000).toFixed(2))}M`
    }
    case value >= 0 && value <= 1000: {
      return formatAmount(value)
    }
    case value >= 1000000000: {
      return `${formatCommas((value / 1000000000).toFixed(2))}MM`
    }
    default: {
      return ''
    }
  }
}

/**
 * Formats a numeric value as a currency string based on the specified currency type.
 */

export const formatCurrency = (amount, currency) => {
  // Helper to apply the desired format with trailing zero removal
  const formatNumber = (value, fractionDigits, showDecimalsOnlyIfNonZero) => {
    const formatter = new Intl.NumberFormat('es-ES', {
      // Ensures thousand separator
      useGrouping: true,
      minimumFractionDigits: showDecimalsOnlyIfNonZero ? 0 : fractionDigits,
      maximumFractionDigits: fractionDigits
    })
    return formatter.format(value)
  }

  switch (currency) {
    case 'USD':
      return `USD ${formatNumber(amount, 2, true)}` // Up to 2 decimals, removing trailing zeros
    case 'EUR':
      return `€ ${formatNumber(amount, 2, true)}` // Up to 2 decimals, removing trailing zeros
    case 'CLF':
      return `UF ${formatNumber(amount, 4, true)}` // Up to 4 decimals, removing trailing zeros
    case 'UTM':
      return `UTM ${formatNumber(amount, 4, true)}` // Up to 4 decimals, removing trailing zeros
    case 'CLP':
      return `$ ${formatNumber(amount, 0, true)}` // No decimals
    default:
      return `${currency} ${formatNumber(amount, 2, false)}` // Default to up to 2 decimals
  }
}

/**
 * Capitalizes the first letter of the given string.
 */
export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1)
